/*
  Editorial header.

  Scroll fade (all breakpoints): the bar is fixed to the top of the viewport.
  It is shown at the top of the page, fades out while scrolling down past
  ~100px (and goes fully click-through so it never intercepts the grid
  beneath), and fades back in on scroll up. The scroll listener is throttled
  with requestAnimationFrame and only toggles a class — the fade itself is a
  CSS transition, which prefers-reduced-motion turns off.

  Mobile overlay (<990px): tapping MENU opens a full-screen drill-down —
  parent items replace the list with their children plus a BACK item. Body
  scroll is locked while open, focus is trapped, and Escape closes it. This
  is untouched by the desktop behaviour below.

  Desktop nav (>=990px): a parent with children stays a plain link. Hovering
  or focusing it reveals its children directly beneath it (position: absolute
  in header-bar.css, so the header's own height never changes). Shown/hidden
  by toggling the `hidden` attribute here, not by CSS :hover — a plain :hover
  reveal can't have a close *delay*, and one's needed: leaving the parent
  starts a short timer rather than closing immediately, so moving the mouse
  diagonally from the parent link down into its children doesn't dismiss it
  along the way. Focus opens/closes immediately (no delay — Tab is discrete,
  not a diagonal mouse path), Escape closes and returns focus to the parent,
  and touch (pointer: coarse, not UA sniffing) gets a first tap that opens
  and a second tap that follows the link.
*/

const HEADER_SHOW_AT_TOP = 100; // px from the top where the bar is always shown
const HEADER_SCROLL_DEADZONE = 4; // px of slack before a direction change counts

class HeaderBar extends HTMLElement {
  constructor() {
    super();

    // ---------- Scroll fade ----------
    this.lastScroll = window.scrollY;
    this.scrollTicking = false;
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    requestAnimationFrame(() => this.updateVisibility());

    // ---------- Mobile overlay ----------
    this.panel = this.querySelector('.header-bar__panel');
    this.toggle = this.querySelector('.header-bar__menu-toggle');
    this.closeButton = this.querySelector('.header-bar__close');
    this.drill = this.querySelector('.header-bar__drill');
    this.panels = this.drill
      ? Array.from(this.drill.querySelectorAll('.header-bar__drill-list'))
      : [];
    this.desktopQuery = window.matchMedia('(min-width: 990px)');

    // ---------- Desktop nav: parents reveal their children on hover/focus ----------
    this.navItems = Array.from(this.querySelectorAll('.header-bar__nav-item--parent'));
    this.setupDesktopNav();

    if (!this.panel || !this.toggle) return;

    this.onKeydown = this.onKeydown.bind(this);

    this.toggle.addEventListener('click', () => this.open());
    this.closeButton?.addEventListener('click', () => this.close());

    // Close after following a link; swap panels when a drill trigger is hit.
    this.panel.addEventListener('click', (event) => {
      const drillTrigger = event.target.closest('[data-drill-to]');
      if (drillTrigger) {
        this.showPanel(drillTrigger.dataset.drillTo);
        return;
      }
      if (event.target.closest('a')) this.close();
    });

    // If the viewport grows past the desktop breakpoint while open, reset.
    this.desktopQuery.addEventListener('change', (event) => {
      if (event.matches) this.close();
      this.resetDesktopNav();
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.onTouchStartOutside) document.removeEventListener('touchstart', this.onTouchStartOutside);
  }

  // ---------- Desktop nav: parents reveal their children on hover/focus ----------

  setupDesktopNav() {
    if (!this.navItems.length) return;

    const HOVER_CLOSE_DELAY = 180; // ms — long enough to cross diagonally, short enough not to feel stuck open
    const closeTimers = new WeakMap();

    // pointer: coarse, not user-agent sniffing — a touchscreen has no
    // hover, even at a viewport width where this desktop nav (not the
    // mobile drill-down) is what's showing (e.g. a tablet in landscape).
    this.isTouchNav = window.matchMedia('(pointer: coarse)').matches;

    const getParts = (item) => ({
      link: item.querySelector(':scope > .header-bar__nav-link'),
      children: item.querySelector(':scope > .header-bar__nav-children'),
    });

    const openItem = (item) => {
      this.navItems.forEach((other) => {
        if (other !== item) closeItem(other);
      });
      clearTimeout(closeTimers.get(item));
      const { link, children } = getParts(item);
      children?.removeAttribute('hidden');
      link?.setAttribute('aria-expanded', 'true');
    };

    const closeItem = (item) => {
      clearTimeout(closeTimers.get(item));
      const { link, children } = getParts(item);
      children?.setAttribute('hidden', '');
      link?.setAttribute('aria-expanded', 'false');
      item.classList.remove('is-touch-open');
    };

    const scheduleClose = (item) => {
      clearTimeout(closeTimers.get(item));
      closeTimers.set(
        item,
        setTimeout(() => closeItem(item), HOVER_CLOSE_DELAY)
      );
    };

    this.navItems.forEach((item) => {
      const { link, children } = getParts(item);
      if (!link || !children) return;

      // Escape closes and moves focus to the parent link — which, being
      // inside `item`, immediately fires focusin again below. `suppressOpen`
      // stops that from silently reopening what Escape just closed; a fresh
      // mouse hover or a later genuine arrival (focusin from outside) both
      // clear it, so the item behaves normally again next time.
      let suppressOpen = false;

      item.addEventListener('mouseenter', () => {
        suppressOpen = false;
        openItem(item);
      });

      item.addEventListener('mouseleave', () => scheduleClose(item));

      item.addEventListener('focusin', () => {
        if (suppressOpen) return;
        openItem(item);
      });

      item.addEventListener('focusout', (event) => {
        if (item.contains(event.relatedTarget)) return;
        suppressOpen = false;
        closeItem(item);
      });

      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (link.getAttribute('aria-expanded') !== 'true') return;
        suppressOpen = true;
        closeItem(item);
        link.focus();
      });

      if (this.isTouchNav) {
        // The parent is a <button> with nowhere to navigate, so a tap just
        // toggles it — no "second tap follows the link" case any more.
        link.addEventListener('click', () => {
          if (item.classList.contains('is-touch-open')) {
            item.classList.remove('is-touch-open');
            closeItem(item);
            return;
          }
          item.classList.add('is-touch-open');
          openItem(item);
        });
      }
    });

    if (this.isTouchNav) {
      this.onTouchStartOutside = (event) => {
        this.navItems.forEach((item) => {
          if (!item.contains(event.target)) closeItem(item);
        });
      };
      document.addEventListener('touchstart', this.onTouchStartOutside, { passive: true });
    }

    this.closeAllDesktopNav = () => this.navItems.forEach(closeItem);
  }

  resetDesktopNav() {
    this.closeAllDesktopNav?.();
  }

  onScroll() {
    if (this.scrollTicking) return;
    this.scrollTicking = true;
    requestAnimationFrame(() => {
      this.updateVisibility();
      this.scrollTicking = false;
    });
  }

  updateVisibility() {
    const y = Math.max(0, window.scrollY);

    // While the mobile menu is open, keep the bar visible and just track
    // the position so the next real scroll compares against the right value.
    if (this.classList.contains('is-open')) {
      this.classList.remove('is-hidden');
      this.lastScroll = y;
      return;
    }

    if (y <= HEADER_SHOW_AT_TOP) {
      this.classList.remove('is-hidden');
    } else if (y > this.lastScroll + HEADER_SCROLL_DEADZONE) {
      this.classList.add('is-hidden'); // scrolling down
    } else if (y < this.lastScroll - HEADER_SCROLL_DEADZONE) {
      this.classList.remove('is-hidden'); // scrolling up
    }

    this.lastScroll = y;
  }

  open() {
    if (this.desktopQuery.matches) return;

    this.classList.add('is-open');
    this.classList.remove('is-hidden');
    this.panel.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add('header-bar-open');

    this.showPanel('root');
    document.addEventListener('keydown', this.onKeydown);
    (this.closeButton || this.panel).focus();
  }

  close() {
    if (!this.classList.contains('is-open')) return;

    this.classList.remove('is-open');
    this.panel.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('header-bar-open');

    document.removeEventListener('keydown', this.onKeydown);
    this.showPanel('root');
    this.toggle.focus();
    this.updateVisibility();
  }

  showPanel(name) {
    if (!this.panels.length) return;

    this.panels.forEach((list) => {
      const active = list.dataset.panel === name;
      list.hidden = !active;
    });

    this.drill.querySelectorAll('.header-bar__drill-parent').forEach((button) => {
      button.setAttribute('aria-expanded', button.dataset.drillTo === name ? 'true' : 'false');
    });

    // Move focus into the panel that just appeared.
    const shown = this.panels.find((list) => list.dataset.panel === name);
    const target = shown && shown.querySelector('a[href], button');
    if (target && this.classList.contains('is-open')) target.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusables = Array.from(
      this.panel.querySelectorAll('a[href], button:enabled')
    ).filter((el) => el.offsetParent !== null);
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define('header-bar', HeaderBar);
